import type { MetaDescriptor } from 'react-router';

import { Duration } from '@boc/duration';
import { generateCacheControlV2 } from '@boc/framework/headers/cache';
import { AttributeInput, ProductFilterInput } from '@boc/framework/saleor-graphql';

import { Route } from './+types/route-jigsaw-puzzles';

import { absoluteify, canonicalize, parseParams, urls } from '~/urls';

import {
  loader as baseLoader,
  headers as baseHeaders,
} from '~/framework/routes/products/routes/search-by-category';

import { ProductResults } from '~/local/products/search/page';
import { brandInfo } from '#/brand';

export { baseHeaders as headers };

// Canonical order for URL segments (prevents duplicate content)
const SEGMENT_ORDER = ['brand', 'series', 'theme', 'artist', 'pieces'] as const;

type SegmentType = (typeof SEGMENT_ORDER)[number];

interface ParsedSegments {
  brand?: string;
  series?: string;
  theme?: string;
  artist?: string;
  pieces?: string;
}

export const loader = async ({ params, request, ...options }: Route.LoaderArgs) => {
  const { channel, locale, saleorLocale } = parseParams(params);

  // Parse glob pattern segments (accept any order)
  const globSegments = params['*'] || '';
  const segments = globSegments.split('/').filter(Boolean);

  const parsed: ParsedSegments = {};

  // Parse segments based on prefixes (order-agnostic)
  segments.forEach((segment) => {
    if (segment.includes('-pieces')) {
      parsed.pieces = segment;
    } else if (segment.startsWith('by-')) {
      parsed.artist = segment.replace('by-', '');
    } else if (segment.startsWith('series-')) {
      parsed.series = segment.replace('series-', '');
    } else if (segment.startsWith('theme-')) {
      parsed.theme = segment.replace('theme-', '');
    } else if (segment.startsWith('brand-')) {
      parsed.brand = segment.replace('brand-', '');
    }
  });

  // Build canonical URL path in correct order (for meta tag)
  const canonicalSegments: string[] = [];

  if (parsed.brand) canonicalSegments.push(`brand-${parsed.brand}`);
  if (parsed.series) canonicalSegments.push(`series-${parsed.series}`);
  if (parsed.theme) canonicalSegments.push(`theme-${parsed.theme}`);
  if (parsed.artist) canonicalSegments.push(`by-${parsed.artist}`);
  if (parsed.pieces) canonicalSegments.push(parsed.pieces);

  const canonicalPath = canonicalSegments.length > 0 ? canonicalSegments.join('/') + '/' : '';

  // Build forced filters based on URL segments
  const filterAttributes: AttributeInput[] = [];

  if (parsed.brand) {
    filterAttributes.push({
      slug: 'puzzle-publisher',
      values: [parsed.brand],
    });
  }

  if (parsed.series) {
    filterAttributes.push({
      slug: 'puzzle-series',
      values: [parsed.series],
    });
  }

  if (parsed.theme) {
    filterAttributes.push({
      slug: 'puzzle-theme',
      values: [parsed.theme],
    });
  }

  if (parsed.artist) {
    filterAttributes.push({
      slug: 'puzzle-artist',
      values: [parsed.artist],
    });
  }

  if (parsed.pieces) {
    filterAttributes.push({
      slug: 'puzzle-piece-count',
      values: [parsed.pieces],
    });
  }

  const forceFilter: ProductFilterInput =
    filterAttributes.length > 0
      ? {
          attributes: filterAttributes,
        }
      : undefined;

  // Call base category loader with jigsaw-puzzles category and forced filters
  const loaderData = await baseLoader(
    { params: { ...params, categorySlug: 'jigsaw-puzzles' }, request, ...options },
    forceFilter
  );

  return {
    ...loaderData,
    ...parsed,
    canonicalPath,
  };
};

export const meta: Route.MetaFunction = ({ data, params }) => {
  const { brand, series, theme, artist, pieces, category, canonicalPath } = data || {};
  const { channel, locale } = parseParams(params);

  // Build title parts in order
  const titleParts: string[] = [];

  if (brand) titleParts.push(brand.replace(/-/g, ' '));
  if (series) titleParts.push(series.replace(/-/g, ' '));
  if (theme) titleParts.push(theme.replace(/-/g, ' '));
  if (artist) titleParts.push(artist.replace(/-/g, ' '));
  if (pieces) titleParts.push(pieces.replace(/-/g, ' '));

  // Capitalize first letter of each word for brand names
  const capitalize = (str: string) =>
    str
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

  // Enhanced title generation with intent keywords
  let title = '';

  if (titleParts.length > 0) {
    const formattedParts = titleParts.map(capitalize).join(' ');

    // Check if it's for adults (1000+ pieces or no piece count specified with brand/theme)
    const isForAdults = pieces?.includes('1000') || pieces?.includes('1500') || pieces?.includes('2000') || (!pieces && (brand || theme));

    // Add quality signal for premium brands
    const isPremiumBrand = brand?.toLowerCase().includes('ravensburger') || brand?.toLowerCase().includes('heye');
    const qualityPrefix = isPremiumBrand ? 'Premium ' : '';

    title = `Buy ${qualityPrefix}${formattedParts} Jigsaw Puzzles${isForAdults ? ' for Adults' : ''} Online`;
  } else {
    // Base category page - comprehensive title
    title = 'Buy Premium Jigsaw Puzzles Online | 100-10000 Pieces for Adults & Kids';
  }

  // Apply channel SEO transformation (adds "in India", "in UAE", etc.)
  title = channel.seo?.title ? channel.seo.title(title) : title;

  // Add brand name to title
  title = `${title} only at ${brandInfo.name}`;

  // Enhanced description generation with transactional keywords and quality signals
  let description = '';

  if (titleParts.length > 0) {
    const formattedParts = titleParts.map(capitalize).join(' ');

    // Add quality signal
    const isPremiumBrand = brand?.toLowerCase().includes('ravensburger') || brand?.toLowerCase().includes('heye');
    const qualitySignal = isPremiumBrand ? 'Premium quality' : 'High-quality';

    // Build description
    const pieceInfo = pieces?.includes('1000') ? 'Perfect for adult puzzle enthusiasts. ' : '';
    const brandInfo = isPremiumBrand ? 'Authentic imported puzzles. ' : '';

    description = `Shop ${qualitySignal.toLowerCase()} ${formattedParts.toLowerCase()} jigsaw puzzles online. ${pieceInfo}${brandInfo}Best prices guaranteed. Same-day dispatch available.`;
  } else {
    // Base category description
    description =
      'Buy the best jigsaw puzzles online. Premium quality puzzles from 100 to 10000 pieces for adults and kids. Ravensburger, Heye & exclusive Indian artist designs. Best prices & fast shipping.';
  }

  // Apply channel description transformation (adds shipping info and cities)
  description = channel.seo?.description ? channel.seo.description(description) : description;

  // Add brand name to description
  if (!description.includes(brandInfo.name)) {
    description = `${description} Shop only at ${brandInfo.name}.`;
  }

  // Generate meta keywords with geo targeting
  const countryName = channel.name.toLowerCase();
  const keywords = [
    'jigsaw puzzles',
    'buy puzzles online',
    pieces ? pieces.replace(/-/g, ' ') : '1000 piece puzzle',
    brand ? `${brand.replace(/-/g, ' ')} puzzles` : null,
    `puzzles ${countryName}`,
    `jigsaw puzzles ${countryName}`,
    'adult puzzles',
    'premium jigsaw puzzles',
    theme ? `${theme.replace(/-/g, ' ')} puzzles` : null,
  ]
    .filter(Boolean)
    .join(', ');

  // Absolute URL from the channel's URL slug. `channel` is an object, and interpolating it
  // published "/[object Object]/en/..." as the canonical of every jigsaw category page.
  const canonicalUrl = canonicalize({
    pathname: `/${channel.urlSlug}/${locale}/products/categories/jigsaw-puzzles/${canonicalPath || ''}`,
    search: '',
    hash: '',
  });

  // Build breadcrumb structured data. Items must be absolute URLs: Search Console rejects
  // relative paths in `itemListElement.item`.
  const breadcrumbList = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: absoluteify(urls.home(params, {})),
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Jigsaw Puzzles',
        item: absoluteify(urls.products.categories(params, { slug: 'jigsaw-puzzles' })),
      },
      ...(brand
        ? [
            {
              '@type': 'ListItem',
              position: 3,
              name: capitalize(brand.replace(/-/g, ' ')),
              item: absoluteify(urls.products.jigsawPuzzles(params, { brand })),
            },
          ]
        : []),
      ...(series
        ? [
            {
              '@type': 'ListItem',
              position: brand ? 4 : 3,
              name: capitalize(series.replace(/-/g, ' ')),
              item: absoluteify(urls.products.jigsawPuzzles(params, { brand, series })),
            },
          ]
        : []),
      ...(theme && !series
        ? [
            {
              '@type': 'ListItem',
              position: brand ? 4 : 3,
              name: capitalize(theme.replace(/-/g, ' ')),
              item: absoluteify(urls.products.jigsawPuzzles(params, { brand, theme })),
            },
          ]
        : []),
    ],
  };

  const metaTags: MetaDescriptor[] = [
    { title },
    { name: 'description', content: description },
    { name: 'keywords', content: keywords },
    { property: 'og:title', content: title },
    { property: 'og:description', content: description },
    { tagName: 'link', rel: 'canonical', href: canonicalUrl },
    { 'script:ld+json': breadcrumbList },
  ];

  return metaTags;
};

export default ProductResults;
